import './MainPage.scss';
import React, {useState, useEffect} from "react";
import {createChannelPlaceholder} from "./MainPage.utils";
import PrimaryClockSection from "./components/PrimaryClockSection";
import SecondaryClocksSection from "./components/SecondaryClocksSection";
import RelaySettingsSection from "./components/RelaySettingsSection";
import ChimeSettingsSection from "./components/ChimeSettingsSection";
import {ClockStation} from "../../../Targets";
import {ReadImpulse} from "../../../Commands";
import {NumberToBitArray} from "../../../Converters";
import Loader from "../../shared/Loader/Loader";
import {SharedApi} from "../../../services/Shared.api";
import UpsSection from "./components/UpsSection";
import MechanismSection from "./components/MechanismSection";
import DimmerSection from "./components/DimmerSection";
import ElectronicClockSection from "./components/ElectronicClockSection";
import SensorBlockSection from "./components/SensorBlockSection";
import BksTimeSection from "./components/BksTimeSection";
import {RelaySettingsApi} from "../../../services/RelaySettings.api";
import {SecondaryClockParams} from "../SecondaryClock/SecondaryClock.model";
import {SecondaryClockApi} from "../../../services/SecondaryClock.api";
import { RelayModeValue } from "../RelaySettings/RelaySettings.model";
import {
  BlockList,
  ChannelDataItem,
  ChannelDescriptions,
  ClockStates,
  DevicesResponse,
  ExternalVisibleDevice,
  MainPageProps,
  ReadImpulseResult,
  RelayDescriptions,
  RelayStatus,
  RelayStatusResponse
} from "./MainPage.model";

const MainPage = ({
                    deviceId,
                    channels = 0,
                    onTabChange,
                    executeCommand,
                    isNewVersion,
                    dawnTime,
                    duskTime,
                    device
                  }: MainPageProps) => {
  const [clockStates, setClockStates] = useState<ClockStates>({1: false, 2: false, 3: false, 4: false});
  const [voltage, setVoltage] = useState<number | undefined>(undefined);
  const [channelData, setChannelData] = useState<ChannelDataItem[]>([
    createChannelPlaceholder(0) as ChannelDataItem,
    createChannelPlaceholder(1) as ChannelDataItem,
    createChannelPlaceholder(2) as ChannelDataItem,
    createChannelPlaceholder(3) as ChannelDataItem
  ]);
  const [channelDescriptions, setChannelDescriptions] = useState<ChannelDescriptions>({});
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [blockList, setBlockList] = useState<BlockList | null>(null);
  const [relayDescriptions, setRelayDescriptions] = useState<RelayDescriptions>({
    channel1: "",
    channel2: ""
  });
  const [relayStatus, setRelayStatus] = useState<RelayStatus>({
    channel1: false,
    channel2: false,
    amplifierRelayEnabled: false,
    speakerRelayEnabled: false,
    relays: {
      "0": {
        enabled: false,
        mode: RelayModeValue.Disabled
      },
      "1": {
        enabled: false,
        mode: RelayModeValue.Disabled
      }
    }
  });

  const [clocks, setClocks] = useState<ExternalVisibleDevice[]>([]);
  const [electronicClocks, setElectronicClocks] = useState<ExternalVisibleDevice[]>([]);
  const [dimmers, setDimmers] = useState<ExternalVisibleDevice[]>([]);
  const [ups, setUps] = useState<ExternalVisibleDevice[]>([]);
  const [sensors, setSensors] = useState<ExternalVisibleDevice[]>([]);
  const [isClockStation, setIsClockStation] = useState<boolean | undefined>(undefined);
  const [isRelayEnabled, setIsRelayEnabled] = useState(false);


  useEffect(() => {
    if (device?.clockStation === undefined) {
      setIsClockStation(undefined);
    } else {
      setIsClockStation(device?.clockStation !== null);
    }
  }, [device?.clockStation]);

  useEffect(() => {
    if (isClockStation === undefined) return;
    if (!isClockStation) return;

    getBlockInfo().then();
    fetchRelayStatus().then();
    fetchRelayDescriptions().then();
    readParameters().then();

  }, [isClockStation, deviceId])

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>, channelIndex: number) => {
    const value = e.target.value;
    const [hours, minutes] = value.split(':').map(Number);

    const newChannelData = structuredClone(channelData);
    newChannelData[channelIndex].hours = hours;
    newChannelData[channelIndex].minutes = minutes;
    newChannelData[channelIndex].timeChanged = true;

    setChannelData(newChannelData);
  };

  const handleTimeWrite = (channelIndex: number) => {
    const newChannelData = structuredClone(channelData);
    newChannelData[channelIndex].timeChanged = false;
    setChannelData(newChannelData);
  };

  const handleRefreshData = async (_parametersResponse?: unknown, _impulseArray?: unknown) => {
    await readParameters();
  };


  const readParameters = async () => {
    if (isClockStation === false) return;

    setLoading(true);
    try {
      const response = await SecondaryClockApi.getParams(String(deviceId)) as SecondaryClockParams;

      setVoltage(response?.voltage);

      const newChannelData = structuredClone(channelData);

      const resultImpulse = await executeCommand(ClockStation, ReadImpulse, []) as ReadImpulseResult;
      const arr = NumberToBitArray(resultImpulse.Params[5]);

      const newClockStates = {...clockStates};

      (response.channels ?? []).forEach((channel, index: number) => {
        if (channel && index < newChannelData.length) {
          newChannelData[index].hours = channel.arrowPosition?.hour ?? 0;
          newChannelData[index].minutes = channel.arrowPosition?.minute ?? 0;
          newChannelData[index].amperage = channel.current;
          newChannelData[index].state = channel.state ?? '';
          newChannelData[index].timeChanged = false;

          const isEnabled = !!arr[7 - index];
          newChannelData[index].enabled = isEnabled;

          newClockStates[index + 1] = isEnabled;
        }
      });

      setChannelData(newChannelData);
      setClockStates(newClockStates);
      setInitialized(true);

    } catch (error) {
      console.error('Ошибка при получении параметров:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBlockInfo = async () => {
    if (isClockStation === false) return;

    const blocksList = await SharedApi.getBlocks(String(deviceId));
    setBlockList(blocksList);
  }

  const handleToggleChange = (clockId: number, checked: boolean) => {
    setClockStates(prev => ({
      ...prev,
      [clockId]: checked
    }));

    const channelIndex = clockId - 1;
    if (channelIndex >= 0 && channelIndex < channelData.length) {
      const newChannelData = [...channelData];
      newChannelData[channelIndex].enabled = checked;
      setChannelData(newChannelData);
    }
  };

  const fetchRelayDescriptions = async () => {
    if (isClockStation === false) return;

    try {
      const data = await RelaySettingsApi.getRelayDescription(String(deviceId));
      if (data.relays) {
        setRelayDescriptions({
          channel1: data.relays["0"]?.description || "",
          channel2: data.relays["1"]?.description || ""
        });
      }
    } catch (error) {
      console.error('Ошибка при загрузке описаний реле:', error);
    }
  };

  const fetchRelayStatus = async () => {
    if (isClockStation === false) return;

    try {
      const response = await RelaySettingsApi.getRelayStatus(String(deviceId)) as RelayStatusResponse;
      if (response.relays) {
        setIsRelayEnabled(Boolean(response?.relays['0']?.enabled || response?.relays['1']?.enabled));

        setRelayStatus({
          channel1: response.relays["0"]?.enabled || false,
          channel2: response.relays["1"]?.enabled || false,
          amplifierRelayEnabled: response.amplifierRelayEnabled || false,
          speakerRelayEnabled: response.speakerRelayEnabled || false,
          relays: {
            "0": {
              enabled: response.relays["0"]?.enabled || false,
              mode: response.relays["0"]?.mode || RelayModeValue.Disabled
            },
            "1": {
              enabled: response.relays["1"]?.enabled || false,
              mode: response.relays["1"]?.mode || RelayModeValue.Disabled
            }
          }
        });
      } else {
        setRelayStatus(prev => ({
          ...prev,
          channel1: response.channel1 || false,
          channel2: response.channel2 || false,
          amplifierRelayEnabled: response.amplifierRelayEnabled || false,
          speakerRelayEnabled: response.speakerRelayEnabled || false
        }));
      }
    } catch (error) {
      console.error('Ошибка при загрузке статуса реле:', error);
    }
  };

  const getDevices = async () => {
    setLoading(true);

    try {
      const result = await SharedApi.getDevices(String(deviceId)) as DevicesResponse;

      setClocks(result.clocks.filter((c) => c.isVisible));
      setDimmers(result.dimmers.filter((d) => d.isVisible));
      setUps(result.ups.filter((u) => u.isVisible));
      setElectronicClocks(result.electronicClocks.filter((e) => e.isVisible));
      setSensors(result.sensors.filter((e) => e.isVisible));
    } catch (error) {
      console.error('Ошибка :', error);
      throw error;
    } finally {
      setLoading(false)
    }
  }


  const loadChannelDescription = async (channelNumber: number) => {
    try {
      const description = await SecondaryClockApi.getDetails(String(deviceId), channelNumber + 1);
      setChannelDescriptions(prev => ({
        ...prev,
        [channelNumber]: {
          installationLocation: description.installationLocation ?? undefined,
        }
      }));
    } catch (error) {
      console.error(`Ошибка при загрузке описания канала ${channelNumber + 1}:`, error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!initialized) {
        await getDevices()

      }
    };

    loadData().then();
  }, [deviceId, initialized]);

  useEffect(() => {
    if (initialized) {
      availableChannels.forEach(channel => {
        loadChannelDescription(channel.channel).then();
      });
    }
  }, [initialized]);


  const availableChannels = channelData.filter((channelData, index) => index < channels);

  return (
    <div>
      {device?.isConnected === undefined ? (
        <div className="loader-wrapper">
          <Loader/>
        </div>
      ) : !device?.isConnected ? (
        <div className="loader-wrapper">
          <h1>Не в сети</h1>
        </div>

      ) : (!blockList && device?.clockStation) ? (
        <div className="loader-wrapper">
          <Loader/>
        </div>
      ) : (
        <form>

          {/*Первичные часы*/}
          {isClockStation
            ? <PrimaryClockSection deviceId={String(deviceId)} onTabChange={onTabChange}/>
            : <BksTimeSection deviceId={String(deviceId)} onTabChange={onTabChange}/>
          }


          {/*Вторичные часы*/}
          {isClockStation && (
            (!blockList?.channelLocks?.[0] || !blockList?.channelLocks?.[1]) && (
              <SecondaryClocksSection
                availableChannels={availableChannels}
                channelDescriptions={channelDescriptions}
                clockStates={clockStates}
                voltage={voltage}
                channelData={channelData}
                loading={loading}
                initialized={initialized}
                onTimeChange={handleTimeChange}
                onToggleChange={handleToggleChange}
                executeCommand={executeCommand}
                deviceId={String(deviceId)}
                onTabChange={onTabChange}
                onTimeWrite={handleTimeWrite}
                onRefreshData={handleRefreshData}
                blockedChannels={blockList?.channelLocks}
                relayStatus={relayStatus}
              />
            )
          )}


          {/*Реле*/}
          {isClockStation && (
            !blockList?.scheduleLock && (
              <RelaySettingsSection
                deviceId={deviceId}
                relayDescriptions={relayDescriptions}
                relayStatus={relayStatus}
                isNewVersion={isNewVersion}
                onTabChange={onTabChange}
                executeCommand={executeCommand}
                dawnTime={dawnTime}
                duskTime={duskTime}
              />
            )
          )}


          {/*Бой*/}
          {isClockStation && (
            (!blockList?.clockChimesLock && device?.clockStation?.withMelody) && (
              <ChimeSettingsSection
                onTabChange={onTabChange}
                deviceId={String(deviceId)}
                relayStatus={relayStatus as any}
              />
            )
          )}

          <div className="row">
            {/*ИБП*/}
            {ups?.length > 0 && (
              ups.map((u) => (
                <div key={u?.id} className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
                  <UpsSection ups={u as any} deviceId={String(deviceId)}/>
                </div>
              ))
            )}

            {/*{Умный часовой механизм}*/}
            {clocks?.length > 0 && clocks?.map((clock) => (
              <div key={clock?.id} className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
                <MechanismSection clock={clock as any} deviceId={String(deviceId)} isRelayEnabled={isRelayEnabled}/>
              </div>
            ))}

            {/*{Диммер}*/}
            {dimmers?.length > 0 && dimmers?.map((dimmer) => (
              <div key={dimmer.id} className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                <DimmerSection dimmer={dimmer as any} deviceId={String(deviceId)}/>
              </div>
            ))}

            {/*{Блок датчиков}*/}
            {sensors?.length > 0 && sensors?.map((sensor) => (
              <div key={sensor.id} className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                <SensorBlockSection sensor={sensor as any} deviceId={String(deviceId)}/>
              </div>
            ))}

            {/*{Электронные часы}*/}
            {electronicClocks?.length > 0 && electronicClocks?.map((clock) => (
              <div key={clock.id} className="col-lg-6 col-md-12 col-sm-12 col-xs-12">
                <ElectronicClockSection electronicClock={clock as any} deviceId={String(deviceId)}/>
              </div>
            ))}

          </div>


          {/*Техническая информация */}
          {/*<TechnicalFieldSection />*/}
        </form>
      )}
    </div>
  );
};

export default MainPage;
